import { Theme } from '@emotion/react';

/**
 * Цвет тени уровня. Это тот же `SurfaceRaisinBlack50`, что в `colors`, — но взять
 * его отсюда ссылкой нельзя: объект ещё не создан. Через `color-mix` от литерала,
 * а не через `var(--color-*)`, чтобы значение не зависело от того, попала ли
 * переменная в собранный `:root`.
 */
const ELEVATION_SHADOW_COLOR = 'color-mix(in srgb, #1A1A29 8%, transparent)';

/**
 * Примитивы, из которых собраны роли (`surface*` / `ink*` / `line*`). Объявлены
 * константой выше `theme`, чтобы роль **ссылалась** на поле палитры, а не
 * повторяла литерал: разойтись значение роли и её примитива теперь не могут.
 *
 * Здесь только те примитивы, у которых есть роль-эквивалент. Остальная палитра
 * по-прежнему живёт литералами в `colors` — переносить её сюда целиком незачем.
 */
const palette = {
  white: '#FFFFFF',
  cultured10: '#F4F4F5',
  platinum20: '#EAEAEC',
  black24: '#242424',
  darkBlue: '#1A1A29',
  gray52: '#52525B',
  gray: '#686A79',
  gray96: '#969696',
  whiteGray50: '#9093AA80',
};

export const theme: Theme = {
  colors: {
    SurfaceIndependence40: '#2D2D47',
    SurfaceCultured10: palette.cultured10,
    SurfacePlatinum20: palette.platinum20,
    SurfaceRomanSilver30: '#8F8E99',
    SurfaceRaisinBlack50: '#1A1A29',
    SurfaceRichBlack60: '#0A0A0B',
    SurfaceWhite: palette.white,
    SurfaceSpaceCadet50: '#23233B',

    // --- Роли -------------------------------------------------------------
    // Роль отвечает на вопрос «что этим красят», примитив — «какой это цвет».
    // В новом коде берём роль; примитив остаётся для того, у чего роли нет
    // (цвета состояний, акценты, канва). Значения роли и примитива совпадают
    // по построению — см. `palette` выше.

    /** Фон страницы, из которого «вырезаны» карточки. */
    SurfacePage: palette.cultured10,
    /** Поверхность поверх фона: карточка, строка списка, шапка, модалка. */
    SurfaceCard: palette.white,
    /** Утопленный блок на карточке: код, шапка полотна, неактивная вкладка. */
    SurfaceSunken: palette.platinum20,

    /**
     * Основной текст. 15.61:1 на SurfacePage, 17.16:1 на SurfaceCard.
     *
     * Это `DarkBlue`, а не `Black24`: базовый класс `.text` красит **весь**
     * `UDText` в `var(--color-DarkBlue)` (`src/styles/typography.scss:3`), то
     * есть основной текст приложения де-факто уже этот цвет. Роль обязана
     * описывать реальность, иначе она врёт про себя, а guard на «примитив
     * вместо роли» упирается в собственную же тему. `Black24` остаётся
     * примитивом без роли — им красит `template-editor` со своей палитрой.
     */
    InkPrimary: palette.darkBlue,
    /** Второстепенный текст: пояснение, подпись, метка. 7.03:1 на SurfacePage. */
    InkSecondary: palette.gray52,
    /**
     * Приглушённый, но всё ещё читаемый текст: мета-строка. 4.87:1 на
     * SurfacePage, 5.35:1 на SurfaceCard — но всего 4.45:1 на SurfaceSunken:
     * внутри утопленного блока приглушённый текст берёт InkSecondary.
     */
    InkMuted: palette.gray,
    /**
     * Только выключенный контрол и плейсхолдер. AA не проходит (2.69:1 на
     * SurfacePage) — и не обязан: WCAG не требует контраста от disabled.
     * Всё, что читают, берёт InkPrimary / InkSecondary / InkMuted.
     */
    InkDisabled: palette.gray96,
    /** Текст на тёмной поверхности: боковое меню, табло, тултип. */
    InkOnDark: palette.white,

    /** Разделитель и граница карточки. */
    LineSubtle: palette.platinum20,
    /** Акцентная граница: фокус, выделенная рамка. */
    LineStrong: palette.whiteGray50,
    // ----------------------------------------------------------------------

    error: '#FF7061',
    submit: '#57CD9C',
    // Предупреждение. Читаем только на тёмных поверхностях (7.06:1 на Black24,
    // 7.81:1 на SurfaceRaisinBlack50). На белом даёт 2.20:1 — текстом на светлом
    // фоне не использовать, там нужен тёмный оттенок из шкалы DarkYellow.
    warning: '#FF9500',

    // Исходы заявки. У каждого тона четыре роли: базовый цвет (граница и ховер),
    // светлая заливка неактивного чипа, насыщенная — активного, и тёмный текст.
    // Базы — существующие цвета темы: CaseSuccess === submit, CaseError === error,
    // CasePending === AccentNaplesYellow; оттенки выведены из них, чтобы три тона
    // читались как одна шкала. Контраст текста: на своей светлой заливке 5.4–5.7:1,
    // на насыщенной 4.6–4.8:1.
    CaseSuccess: '#57CD9C',
    CaseSuccessSurface: '#E7F8F1',
    CaseSuccessSurfaceActive: '#C6EEDD',
    CaseSuccessText: '#2F6F54',

    CaseError: '#FF7061',
    CaseErrorSurface: '#FFEBE9',
    CaseErrorSurfaceActive: '#FFCEC9',
    CaseErrorText: '#99433A',

    CasePending: '#D9B244',
    CasePendingSurface: '#FAF4E5',
    CasePendingSurfaceActive: '#F2E5BF',
    CasePendingText: '#756025',

    AccentNaplesYellow: '#D9B244',
    AccentMediumChampagne: '#FAECAB',
    AccentJonquil: '#F2CA18',
    AccentCornsilk: '#FFF8D9',

    DarkYellow: '#D9B244',
    DarkYellow100: '#F8EDC5',
    DarkYellow200: '#F0D88F',
    DarkYellow300: '#E6C662',
    DarkYellow400: '#DBB749',
    DarkYellow600: '#B89638',
    DarkYellow700: '#94762D',
    DarkYellow800: '#705622',
    DarkYellow900: '#4C3716',

    DarkYellowAlpha80: '#D9B244CC',
    DarkYellowAlpha60: '#D9B24499',
    DarkYellowAlpha40: '#D9B24466',
    DarkYellowAlpha20: '#D9B24433',

    LightGold: '#E8D18F',
    Gold: '#E1C169',

    WhiteGray: '#9093AA',
    WhiteGray60: '#9093AA99',
    WhiteGray50: palette.whiteGray50,
    WhiteGray20: '#9093AA33',
    WhiteGray10: '#9093AA1A',

    BorderGray50: '#F7F8FA',
    DisabledInputColor: '#b5b5b7',
    GrayF2: '#F2F2F2',
    Gray96: palette.gray96,
    // Тёмно-серый для второстепенного текста. Имени в дизайне нет — назван по
    // существующей конвенции (Gray96 = #969696, GrayF2 = #F2F2F2).
    Gray52: palette.gray52,
    Black24: palette.black24,
    DarkBlue: palette.darkBlue,
    DarkBlue100: '#E7E7EC',
    DarkBlue200: '#C2C2D0',
    DarkBlue300: '#9D9DB4',
    DarkBlue400: '#3A3A50',
    DarkBlue600: '#161624',
    DarkBlue700: '#12121F',
    DarkBlue800: '#0E0E1A',
    DarkBlue900: '#0A0A14',

    Blue: '#212738',
    Gray: '#686A79',
    Pink: '#FF2D55',
    Green: '#44D968',
    Black: '#1E1E1E',
    LightBlue: '#008CEA',
    EditorAccentBlue: '#007AFF',
    NarcissusYellow: '#DBB400',
    AlphaWhite: '#FFFFFF99',
    Red: '#FA2427',

    // Затемнение под модалкой и любым другим блокирующим слоем. До введения токена
    // в проекте было три разных: rgba(0,0,0,0.5), rgba(0,0,0,0.4) и #00000085;
    // каноном взято доминирующее — 50% чёрного из ud-ui/components/modal.
    Scrim: 'rgba(0, 0, 0, 0.5)',
  },
  radii: {
    /** Мелкие контролы: чекбокс, чип, бейдж, иконочная кнопка. */
    control: '4px',
    /** Основной радиус проекта: кнопка, поле, карточка, строка списка. */
    card: '8px',
    /** Крупные поверхности: модалка, панель, выпадающее меню. */
    panel: '12px',
    /** Полностью скруглённые: аватар-пилюля, тумблер, счётчик. */
    pill: '9999px',
  },
  elevation: {
    /** Приподнято на шаг: строка при ховере, чип. */
    low: `0px 0px 1px ${ELEVATION_SHADOW_COLOR}, 0px 2px 4px ${ELEVATION_SHADOW_COLOR}`,
    /** Оторвано от фона: выпадашка, поповер, тултип. */
    medium: `0px 0px 1px ${ELEVATION_SHADOW_COLOR}, 0px 4px 8px ${ELEVATION_SHADOW_COLOR}`,
    /** Блокирующий слой: модалка, дровер. */
    high: `0px 0px 1px ${ELEVATION_SHADOW_COLOR}, 0px 8px 16px ${ELEVATION_SHADOW_COLOR}`,
  },
  /**
   * Шкала слоёв приложения — только для тех, кто видит друг друга. Шаг 100, чтобы
   * промежуточный слой не требовал перенумерации. Локальный стекинг внутри одного
   * компонента (`z-index: -1` на псевдоэлементе, `1` на активной строке) токеном
   * не оформляется: это деталь вёрстки, а не слой приложения.
   */
  zIndex: {
    base: 0,
    content: 1,
    sticky: 100,
    dropdown: 200,
    modal: 300,
    toast: 400,
  },
  fonts: {
    'caption-1': {
      'font-size': '12px',
      'font-weight': 'normal',
      'font-family': 'SF Pro Text, SF Pro Display, sans-serif',
    },
  },
};
